import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Masonry from "react-masonry-css";
import { Search, LayoutGrid, Image as ImageIcon, Film } from "lucide-react";
import api from "../lib/api";
import { accentFor } from "../lib/accents";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AdCard } from "../components/AdCard";
import { AdDetailModal } from "../components/AdDetailModal";
import { AdEditModal } from "../components/AdEditModal";

const BREAKPOINTS = { default: 4, 1280: 3, 768: 2, 500: 1 };
const SORTS = [
  { value: "custom", label: "Curated order" },
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "popular", label: "Most downloaded" },
];

export default function Library() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState(null);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [mediaType, setMediaType] = useState(""); // "" | "image" | "video"
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("custom");
  const [selected, setSelected] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const isAdmin = user?.role === "admin";
  const deepLinkAdId = searchParams.get("ad");

  // Sync category state to URL so /?category=<id> deep-links work
  useEffect(() => {
    const params = Object.fromEntries(searchParams);
    if (category) params.category = category; else delete params.category;
    setSearchParams(params, { replace: true });
  }, [category]);

  // Deep-link support: /?ad=<id> auto-opens the ad detail modal after ads load
  useEffect(() => {
    if (!deepLinkAdId || !ads?.length) return;
    const match = ads.find((a) => a.id === deepLinkAdId);
    if (match) {
      setSelected(match);
      const params = Object.fromEntries(searchParams);
      delete params.ad;
      setSearchParams(params, { replace: true });
    } else {
      // Ad isn't in the current filtered view — fetch it directly and open
      api.get(`/ads/${deepLinkAdId}`).then((r) => setSelected(r.data)).catch(() => {});
      const params = Object.fromEntries(searchParams);
      delete params.ad;
      setSearchParams(params, { replace: true });
    }
  }, [deepLinkAdId, ads]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const fetchAds = useCallback(() => {
    const params = { sort };
    if (category) params.category = category;
    if (mediaType) params.media_type = mediaType;
    if (search) params.search = search;
    api.get("/ads", { params }).then((r) => setAds(r.data)).catch(() => setAds([]));
  }, [category, mediaType, search, sort]);

  useEffect(() => {
    const t = setTimeout(fetchAds, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchAds, search]);

  const onFavoriteChange = (adId, isFav) => {
    setAds((prev) => prev?.map((a) => (a.id === adId ? { ...a, is_favorite: isFav } : a)));
    setSelected((prev) => (prev && prev.id === adId ? { ...prev, is_favorite: isFav } : prev));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      {user && user.role !== "admin" && typeof user.days_remaining === "number" && user.days_remaining <= 3 && user.days_remaining >= 0 && user.expires_at && (
        <div className="bg-[#DF1283] text-white px-6 py-3 text-sm text-center font-semibold" data-testid="expiry-warning-banner">
          Your access {user.days_remaining === 0 ? "expires today" : user.days_remaining === 1 ? "expires tomorrow" : `expires in ${user.days_remaining} days`} — <a href="mailto:hello@unclonedmarketing.com" className="underline hover:text-[#D0FF0D]">contact us to renew</a>.
        </div>
      )}
      <main className="max-w-7xl mx-auto px-6 py-10 flex-1">
        <div className="fade-up">
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#000000] tracking-tight">The Ad Wall</h1>
          <p className="text-gray-500 mt-2">Curated ad creatives, ready for every placement.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-8">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="library-search-input"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, hook, angle, offer..."
              className="pl-10 pr-4 py-2.5 rounded-full border border-gray-300 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] focus:border-transparent"
            />
          </div>
          <select
            data-testid="library-sort-select"
            value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-gray-300 text-sm px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="inline-flex rounded-full border border-gray-300 bg-white p-1" role="tablist" data-testid="library-media-tabs">
            {[
              { key: "", label: "All", Icon: LayoutGrid, testid: "media-tab-all" },
              { key: "image", label: "Image Ads", Icon: ImageIcon, testid: "media-tab-image" },
              { key: "video", label: "Video Ads", Icon: Film, testid: "media-tab-video" },
            ].map(({ key, label, Icon, testid }) => (
              <button
                key={key || "all"}
                data-testid={testid}
                role="tab"
                aria-selected={mediaType === key}
                onClick={() => setMediaType(key)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.08em] transition-colors ${
                  mediaType === key
                    ? "bg-[#000000] text-white"
                    : "text-gray-500 hover:text-[#000000]"
                }`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <button
            data-testid="category-chip-all"
            onClick={() => setCategory("")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${!category ? "bg-[#000000] text-white" : "bg-gray-100 text-[#000000] hover:bg-gray-200"}`}
          >
            All
          </button>
          {categories.map((c) => {
            const a = accentFor(c.color);
            const active = category === c.id;
            return (
              <button
                key={c.id}
                data-testid={`category-chip-${c.slug}`}
                onClick={() => setCategory(active ? "" : c.id)}
                className="px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200"
                style={active ? { background: a.hex, color: a.text } : { background: "#F3F4F6", color: "#000000" }}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        {ads === null ? (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#0FFFE2] spin-slow" />
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-24" data-testid="library-empty-state">
            <p className="font-display font-bold text-xl text-[#000000]">No ads found</p>
            <p className="text-gray-500 mt-2 text-sm">Try a different search or category.</p>
          </div>
        ) : (
          <Masonry breakpointCols={BREAKPOINTS} className="masonry-grid mt-10" columnClassName="masonry-col">
            {ads.map((ad, i) => (
              <AdCard key={ad.id} ad={ad} index={i} categories={categories} onOpen={setSelected} onFavoriteChange={onFavoriteChange} />
            ))}
          </Masonry>
        )}
      </main>
      <Footer />
      {selected && (
        <AdDetailModal
          ad={selected}
          categories={categories}
          onClose={() => setSelected(null)}
          onFavoriteChange={onFavoriteChange}
          onNavigate={(dir) => {
            if (!ads?.length) return;
            const idx = ads.findIndex((a) => a.id === selected.id);
            const next = ads[idx + dir];
            if (next) setSelected(next);
          }}
          hasPrev={ads && ads.findIndex((a) => a.id === selected.id) > 0}
          hasNext={ads && ads.findIndex((a) => a.id === selected.id) < ads.length - 1}
          isAdmin={isAdmin}
          onEdit={(ad) => {
            setSelected(null);
            setEditingAd(ad);
          }}
          onCategoryClick={(catId) => {
            setCategory(catId);
            setSelected(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          onNoteCountChange={(adId, count) => {
            setAds((prev) => (prev ? prev.map((a) => (a.id === adId ? { ...a, note_count: count } : a)) : prev));
            if (selected && selected.id === adId) setSelected({ ...selected, note_count: count });
          }}
        />
      )}
      {isAdmin && (
        <AdEditModal
          ad={editingAd}
          categories={categories}
          onClose={() => setEditingAd(null)}
          onSaved={(updated) => {
            setAds((prev) => (prev ? prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)) : prev));
            if (selected && selected.id === updated.id) setSelected({ ...selected, ...updated });
          }}
        />
      )}
    </div>
  );
}
